<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import VuePdfEmbed from "vue-pdf-embed";

import { useDefaultToast } from "@/composables/useDefaultToast";
import {
    DEFAULT_MAX_MO,
    downloadLink,
    getFilenameFromPath,
    isImageSource,
    isPdfSource,
    isWordDocumentSource,
    mimeTypeMap,
    validateBase64Signature,
} from "@/helpers/file";
import { resolveUrl } from "@/helpers/s3";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";

import Dropzone from "@/components/Dropzone.vue";

import EmptyStateIllustration from "@/assets/illustrations/empty-state.svg";

enum ManagedFileType {
    PDF = "pdf",
    IMG = "img",
    UNSUPPORTED = "unsupported",
    EMPTY = "",
}

interface PdfManagerProps {
    readonly?: boolean;
    showActions?: boolean;
    showDownload?: boolean;
    path: string;
    extensionAccept?: string[];
    showRules?: boolean;
    showDropzoneRules?: boolean;
}

const props = withDefaults(defineProps<PdfManagerProps>(), {
    readonly: false,
    showActions: true,
    showDownload: false,
    showRules: false,
    showDropzoneRules: false,
    extensionAccept: () => [".pdf"],
});

const emit = defineEmits<{
    input: [value: unknown];
    add: [doc: string];
    delete: [];
}>();

const emptyFile = { file: "", type: ManagedFileType.EMPTY };

const { t } = useI18n();
const { toastError } = useDefaultToast();

const dropzone = useTemplateRef("dropzone");
// biome-ignore lint/correctness/noUnusedVariables: Init Biome
const pdfContainer = ref<HTMLElement>();
// biome-ignore lint/correctness/noUnusedVariables: Init Biome
const imageDocument = ref<HTMLImageElement>();
const managedFile = ref<{ file: string; type: ManagedFileType }>(emptyFile);
const scale = ref(1);
const isLoadFailed = ref(false);

const noDocument = computed(() => (!props.path || isLoadFailed.value) && props.readonly);

const hasFile = computed(() => managedFile.value.file !== "");
const isZoomable = computed(() => managedFile.value.type === ManagedFileType.PDF || managedFile.value.type === ManagedFileType.IMG);
const isEditable = computed(() => hasFile.value && !props.readonly);
const isDownloadable = computed(() => hasFile.value && (!props.readonly || props.showDownload));

const acceptedMimeTypes = computed(() => mimeTypeMap.filter((m) => props.extensionAccept.includes(m.extension)).map((m) => m.mimeType));

const extensionAcceptFormatted = computed(() => props.extensionAccept?.map((extension) => extension.replace(".", "")).join(", "));

const resolveManagedFileType = (source: string): ManagedFileType => {
    if (isPdfSource(source)) return ManagedFileType.PDF;
    if (isImageSource(source)) return ManagedFileType.IMG;
    if (source) return ManagedFileType.UNSUPPORTED;
    return ManagedFileType.EMPTY;
};

const previewUnavailableMessageKey = computed(() =>
    isWordDocumentSource(props.path) || isWordDocumentSource(managedFile.value.file)
        ? "global.pdfRules.wordPreviewUnavailable"
        : "page.interim.cvIsNotPDF"
);

const save = (doc: string) => {
    if (acceptedMimeTypes.value.length > 0 && !validateBase64Signature(doc, acceptedMimeTypes.value)) {
        toastError(t("global.pdfRules.invalidFileContent"));
        dropzone.value?.reset();
        return;
    }

    const type = resolveManagedFileType(doc);
    managedFile.value = { file: doc, type };
    emit("add", doc);
};

const editPdf = () => {
    dropzone.value?.focusInput();
};

const removePdf = () => {
    managedFile.value = emptyFile;
    isLoadFailed.value = false;
    scale.value = 1;
    dropzone.value?.reset();
    emit("delete");
};

const downloadCV = async () => {
    if (!managedFile.value.file && !props.path) return;

    const fileName = getFilenameFromPath(props.path) || undefined;

    if (!props.path && managedFile.value.file) {
        const randomName = Math.random().toString(36).substring(2, 15);
        downloadLink(managedFile.value.file, fileName ?? randomName);
        return;
    }

    const url = managedFile.value.file || (await resolveUrl(props.path));
    if (url) downloadLink(url, fileName);
};

const zoom = (action: string) => {
    if (action === "+") scale.value += 0.5;
    else if (action === "-" && scale.value > 1) scale.value -= 0.5;

    if (managedFile.value.type === ManagedFileType.PDF) {
        const pdfComponent = document.querySelector(".pdf-manager .pdf-manager__pdf-component");
        if (pdfComponent) {
            (pdfComponent as HTMLElement).style.transform = `scale(${scale.value})`;
        }
    } else if (managedFile.value.type === ManagedFileType.IMG) {
        const img = document.querySelector(".pdf-manager .pdf-manager__image-document");
        if (img) {
            (img as HTMLElement).style.transform = `scale(${scale.value})`;
        }
    }
};

const zoomIn = () => zoom("+");
const zoomOut = () => zoom("-");

watch(
    () => props.path,
    async (newUrl) => {
        scale.value = 1;
        isLoadFailed.value = false;

        if (!newUrl) {
            managedFile.value = emptyFile;
            return;
        }

        try {
            const url = await resolveUrl(newUrl);
            if (!url) {
                managedFile.value = emptyFile;
                isLoadFailed.value = true;
                toastError(t("global.document.error.load.failed"));
                return;
            }
            managedFile.value = { file: url, type: resolveManagedFileType(url) };
        } catch {
            managedFile.value = emptyFile;
            isLoadFailed.value = true;
            toastError(t("global.document.error.load.failed"));
        }
    },
    { immediate: true }
);

defineExpose({ zoomIn, zoomOut, downloadCV, editPdf, removePdf });
</script>
<template>
    <div class="pdf-manager">
        <Dropzone
            v-if="!readonly && !isLoadFailed"
            :formats-hint="showDropzoneRules ? t('global.pdfRules.supportedFormats', { extensions: extensionAcceptFormatted }) : undefined"
            :max-size-hint="showDropzoneRules ? t('global.pdfRules.size', { maxSize: DEFAULT_MAX_MO }) : undefined"
            :accept="extensionAccept"
            v-show="!hasFile"
            ref="dropzone"
            @droped="save"
        />
        <div v-if="managedFile.type === ManagedFileType.PDF || managedFile.type === ManagedFileType.IMG" class="pdf-manager__document-container">
            <div v-if="hasFile && managedFile.type === ManagedFileType.PDF" class="pdf-manager__pdf-container" ref="pdfContainer">
                <VuePdfEmbed :key="managedFile.file" :source="managedFile.file" class="pdf-manager__pdf-component" :disableTextLayer="true" />
            </div>
            <div v-if="managedFile.type === ManagedFileType.IMG" class="pdf-manager__image-container">
                <img class="pdf-manager__image-document" :src="managedFile.file" ref="imageDocument" />
            </div>
        </div>
        <div v-else-if="managedFile.type === ManagedFileType.UNSUPPORTED && hasFile" class="pdf-manager__preview-unavailable">
            <div class="pdf-manager__preview-unavailable-empty-state">
                <EmptyStateIllustration class="pdf-manager__preview-unavailable-illustration" />
            </div>
            <p class="pdf-manager__preview-unavailable-message typo-sm-medium">
                {{ t(previewUnavailableMessageKey) }}
            </p>
            <div class="pdf-manager__preview-unavailable-actions">
                <ButtonAlt
                    type="neutral"
                    variant="stroke"
                    size="sm"
                    left-icon="download"
                    :label="t('global.pdfRules.downloadFile')"
                    @click="downloadCV"
                />
            </div>
        </div>
        <div v-if="showRules" class="pdf-manager__rules">
            <div class="pdf-manager__rules-zone">
                <span class="pdf-manager__rule typo-xxs-regular">
                    {{ t("global.pdfRules.supportedFormats", { extensions: extensionAcceptFormatted }) }}
                </span>
            </div>

            <div class="pdf-manager__rules-zone">
                <span class="pdf-manager__rule typo-xxs-regular">
                    {{ t("global.pdfRules.size", { maxSize: DEFAULT_MAX_MO }) }}
                </span>
            </div>
        </div>
        <div v-if="hasFile && showActions && managedFile.type !== ManagedFileType.UNSUPPORTED" class="actions pdf-manager__actions">
            <ButtonAlt v-if="isDownloadable" size="sm" type="neutral" iconOnly="download" @click="downloadCV" />
            <ButtonAlt v-if="isZoomable" size="sm" type="neutral" iconOnly="zoom-in" @click="zoomIn" />
            <ButtonAlt v-if="isZoomable" size="sm" type="neutral" iconOnly="zoom-out" @click="zoomOut" />
            <ButtonAlt v-if="isEditable" size="sm" type="neutral" iconOnly="edit" @click="editPdf" />
            <ButtonAlt v-if="isEditable" size="sm" type="error" iconOnly="trash" @click="removePdf" />
        </div>
        <div class="noDocument" v-if="noDocument">
            <p>{{ t("page.interim.noDocument") }}</p>
        </div>
    </div>
</template>
<style lang="scss">
.pdf-manager {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md, 8px);
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;

    &__document {
        &-container {
            height: 100%;
            max-width: 100%;
            overflow: auto;
            border-radius: var(--radius-md, 8px);
            border: 1px solid var(--stroke-soft-200, #dbdfe6);
        }

        &-file {
            width: 100%;
            height: auto;
        }
    }

    &__pdf-container {
        width: 100%;
        height: 100%;
        overflow: auto;
    }

    &__pdf-component {
        height: 100%;
        transform-origin: 0 0;
        transition: transform 0.3s ease-in-out;
    }

    &__image-container {
        width: 100%;
        height: 100%;
        overflow: auto;
    }

    &__image-document {
        width: 100%;
        transform-origin: 0 0;
        transition: transform 0.3s ease-in-out;
    }

    &__preview-unavailable {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        padding: 0 var(--spacing-3xl, 24px) var(--spacing-7xl, 64px);
        border-radius: var(--radius-md, 8px);
        text-align: center;
    }

    &__preview-unavailable-empty-state {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        width: 400px;
        max-width: 100%;
    }

    &__preview-unavailable-illustration {
        display: block;
        height: 200px;
        max-width: 100%;
    }

    &__preview-unavailable-message {
        max-width: 400px;
        margin: 0;
        color: var(--text-main-900, #00061a);
    }

    &__preview-unavailable-actions {
        display: flex;
        justify-content: center;
        padding-top: var(--spacing-xl, 16px);
    }

    &__rules {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 0 var(--spacing-md, 8px);
    }

    &__rule {
        color: var(--text-sub-600, #494e61);
    }

    &__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing--xxs, 2px);
        padding: var(--spacing-xs, 4px);
        position: absolute;
        top: var(--spacing-md, 8px);
        right: var(--spacing-md, 8px);
        border-radius: var(--radius-sm, 6px);
        border: 0.5px solid var(--stroke-soft-200, #dbdfe6);
        background-color: var(--bg-white-0, #ffffff);
        box-shadow: var(--regular-shadow-sm, 0 2px 4px 0 rgba(0, 6, 26, 0.04));
    }
}

.noDocument {
    height: 100%;
    width: 100%;
    border: dashed 2px $lightgrey;
    border-radius: 12px;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
}
</style>
